import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AxiosError } from 'axios';

import { Form, FormField, FormTextArea } from 'components/form';
import { emailSchema, messageSchema, nameSchema } from 'utils/validators';
import { GlobalScrollLock } from 'components/GlobalScrollLock';

import { sendReviewInquiry } from '../../api';
import * as styled from '../styles/reviews';

const SUGGESTED_PROMPTS = [
  'Was the service worth the price?',
  'Would you use them again?',
  'Any issues I should know about?',
];

const inquirySchema = z.object({
  sender_name: nameSchema,
  sender_email: emailSchema,
  message: messageSchema,
});
type InquiryFormValues = z.infer<typeof inquirySchema>;

type Props = {
  review: Kudable.Review;
  accentColor: string;
  onClose: () => void;
};

export const InquiryModal = ({ review, accentColor, onClose }: Props) => {
  const form = useForm<InquiryFormValues>({
    resolver: zodResolver(inquirySchema),
    mode: 'onBlur',
    defaultValues: { sender_name: '', sender_email: '', message: '' },
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const { setValue, watch, formState } = form;
  const message = watch('message');

  const handleSubmit = (values: InquiryFormValues) => {
    setSubmitError(null);
    return sendReviewInquiry(review.id, values)
      .then(() => setSent(true))
      .catch((error: AxiosError<{ error?: string }>) => {
        const errorMessage = error.response?.data?.error;
        if (errorMessage?.includes('monthly question limit'))
          setSubmitError(errorMessage);
        else setSubmitError('Could not send your question. Please try again.');
      });
  };

  return (
    <styled.InquiryOverlay onClick={onClose}>
      <GlobalScrollLock />
      <styled.InquiryDialog onClick={(event) => event.stopPropagation()}>
        <styled.InquiryClose onClick={onClose}>✕</styled.InquiryClose>

        {sent ? (
          <styled.InquirySent>
            <styled.InquirySentIcon>✓</styled.InquirySentIcon>
            <styled.InquirySentTitle>Question sent!</styled.InquirySentTitle>
            <styled.InquirySentBody>
              You'll get a reply by email.
            </styled.InquirySentBody>
          </styled.InquirySent>
        ) : (
          <Form form={form} onSubmit={handleSubmit}>
            <styled.InquiryTitle>Ask about this experience</styled.InquiryTitle>
            <styled.InquirySubtitle>
              Send a question to someone who has used this service. Their
              contact details will remain private.
            </styled.InquirySubtitle>

            <styled.InquiryPromptsRow>
              {SUGGESTED_PROMPTS.map((prompt) => (
                <styled.InquiryPrompt
                  key={prompt}
                  type="button"
                  $selected={message === prompt}
                  $accent={accentColor}
                  onClick={() =>
                    setValue('message', prompt, { shouldValidate: true })
                  }
                >
                  {prompt}
                </styled.InquiryPrompt>
              ))}
            </styled.InquiryPromptsRow>

            <FormField name="sender_name" placeholder="Your name" />
            <FormField
              name="sender_email"
              type="email"
              placeholder="Your email"
            />
            <FormTextArea
              name="message"
              placeholder="What would you like to know about their experience?"
              rows={3}
            />

            <styled.InquiryPrivacyNote $accent={accentColor}>
              <styled.InquiryPrivacyIcon>🔒</styled.InquiryPrivacyIcon>
              <div>
                <styled.InquiryPrivacyTitle $accent={accentColor}>
                  Your privacy is protected
                </styled.InquiryPrivacyTitle>
                <styled.InquiryPrivacyBody $accent={accentColor}>
                  Your email will <strong>not</strong> be shared with the
                  reviewer. All replies come through Kudable.
                </styled.InquiryPrivacyBody>
              </div>
            </styled.InquiryPrivacyNote>

            {submitError && (
              <styled.InquiryError>{submitError}</styled.InquiryError>
            )}

            <styled.InquirySendBtn
              type="submit"
              $accent={accentColor}
              disabled={formState.isSubmitting}
            >
              {formState.isSubmitting ? 'Sending...' : 'Send question'}
            </styled.InquirySendBtn>
          </Form>
        )}
      </styled.InquiryDialog>
    </styled.InquiryOverlay>
  );
};
